import { useMemo, useEffect, useState } from "react";
import useDebouncedSearch from "../hooks/useDebouncedSearch";
import FacetPanel from "./FacetPanel";
import SearchResultCard from "./SearchResultCard";
import { sanitizeTerms } from "./searchUtils";
import API_BASE_URL from "../config";
import "./SearchPage.css";

const TYPE_FILTERS = [
  { id: null, label: "Alle" },
  { id: "page", label: "Seiten" },
  { id: "table", label: "Tabellen" },
  { id: "figure", label: "Abbildungen" },
  { id: "reference", label: "Referenzen" },
];

// "Alle" (no document-category filter) is the implicit default; the backend
// /categories endpoint supplies the rest (Zulassung / LV / Katalog / …).
const ALL_CATEGORY = "alle";

export default function SearchPage({
  database = "Betonbau", onAskAboutThis, activeType, setActiveType,
  category, setCategory, initialQuery, onInitialQueryConsumed,
}) {
  // Memoize so the array identity is stable across renders — a fresh literal each
  // render would churn the search hook's callbacks and cause a refetch loop.
  const types = useMemo(() => (activeType ? [activeType] : null), [activeType]);
  const activeCategory = category || ALL_CATEGORY;
  const documentCategory = activeCategory === ALL_CATEGORY ? null : activeCategory;
  const [facetSelection, setFacetSelection] = useState({});
  const { query, setQuery, results, counts, status, error } =
    useDebouncedSearch({ database, types, documentCategory, facets: facetSelection });

  const [categories, setCategories] = useState([]);

  const terms = useMemo(() => sanitizeTerms(query), [query]);
  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

  // Load document-category chips (with per-category document counts) for the
  // selected database. Degrades silently — chips just won't render.
  useEffect(() => {
    let cancelled = false;
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    fetch(`${API_BASE_URL}/api/v1/knowledge/categories${qs}`, {
      headers: { "ngrok-skip-browser-warning": "69420" },
    })
      .then((r) => (r.ok ? r.json() : { categories: [] }))
      .then((d) => { if (!cancelled) setCategories(Array.isArray(d.categories) ? d.categories : []); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [database]);

  // Facet values belong to one workspace: a selection must not leak into another.
  useEffect(() => { setFacetSelection({}); }, [database]);

  // Seed the query from a clicked Recent Search (consumed once).
  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
      onInitialQueryConsumed?.();
    }
  }, [initialQuery, setQuery, onInitialQueryConsumed]);

  useEffect(() => {
    if (status === "done" && query.trim().length >= 2) {
      try {
        const key = "knowledgeRecentSearches";
        const prev = JSON.parse(localStorage.getItem(key) || "[]");
        const next = [query.trim(), ...prev.filter((q) => q !== query.trim())].slice(0, 8);
        localStorage.setItem(key, JSON.stringify(next));
      } catch { /* ignore quota errors */ }
    }
  }, [status, query]);

  return (
    <div className="ds-page search-page">
      <div className="search-top">
        <header className="ds-page-header">
          <h1 className="ds-page-title">Knowledge Search</h1>
          <p className="ds-page-subtitle">Hybride Suche (BM25 + Embeddings) über alle Dokumente</p>
        </header>

        <div className="search-bar">
          <span className="search-icon" aria-hidden="true">🔎</span>
          <input
            type="text"
            aria-label="Wissensdatenbank durchsuchen"
            placeholder="Wissensdatenbank durchsuchen…"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="ds-btn ds-btn--ghost ds-btn--sm clear-btn"
              aria-label="Suche leeren"
              onClick={() => setQuery("")}
            >
              ✕
            </button>
          )}
        </div>

        {/* Document-type categories (Zulassung / LV / Katalog / …) — filters by
            document, orthogonal to the entity-type row below. */}
        {categories.length > 0 && (
          <div className="filter-row" role="group" aria-label="Dokumenttyp">
            {categories.map((c) => {
              const active = activeCategory === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`ds-chip${active ? " is-active" : ""}`}
                  aria-pressed={active}
                  onClick={() => setCategory?.(c.id)}
                  title={`Nach Dokumenttyp filtern: ${c.label}`}
                >
                  {c.label}{" "}
                  {typeof c.count === "number" && <span className="ds-chip-count">{c.count}</span>}
                </button>
              );
            })}
          </div>
        )}

        <FacetPanel database={database} selection={facetSelection} onChange={setFacetSelection} />

        <div className="filter-row" role="group" aria-label="Ergebnisart">
          {TYPE_FILTERS.map((f) => {
            const active = (activeType || null) === f.id;
            const n = f.id ? counts[f.id] : totalCount;
            return (
              <button
                key={f.label}
                type="button"
                className={`ds-chip${active ? " is-active" : ""}`}
                aria-pressed={active}
                onClick={() => setActiveType?.(f.id)}
              >
                {f.label}{" "}
                {status === "done" && n ? <span className="ds-chip-count">{n}</span> : null}
              </button>
            );
          })}
        </div>

        {status === "done" && (
          <div className="search-stats">{totalCount} Treffer</div>
        )}
      </div>

      <div className="results-list">
        {status === "idle" && <div className="ds-empty">Beginnen Sie zu tippen, um zu suchen.</div>}
        {status === "loading" && (
          [0, 1, 2].map((i) => <div key={i} className="result-card skeleton" aria-hidden="true" />)
        )}
        {status === "empty" && <div className="ds-empty">Keine Treffer für „{query}“.</div>}
        {status === "error" && <div className="ds-alert ds-alert--danger" role="alert">Suche fehlgeschlagen: {error}</div>}
        {status === "done" && results.map((r) => (
          <SearchResultCard key={`${r.type}:${r.id}`} result={r} terms={terms}
                            onAskAboutThis={onAskAboutThis} />
        ))}
      </div>
    </div>
  );
}
